import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import omitBy from 'lodash/omitBy';
import { MdFilterList, MdRefresh } from 'react-icons/md';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { DateTime } from 'luxon';
import { asyncState, useAsync, useAuth, useForm } from '~/hooks';
import { MdBook } from 'react-icons/md';
import { policySearchRequest, lawfirm } from '~/api';
import { applyFilters } from './filters';
import { DashboardContext, useDashboard } from './context';
import Header from './DashboardHeader';
import metrics from '~/api/metrics';
import Grid from './DashboardGrid';
import FilterModal from './FilterModal';
import AttachmentsModal from './AttachmentsModal';

const getOffset = (page, pageSize = 10) => {
    return {
        offset: page * pageSize,
        size: pageSize
    };
};

const TryonHome = () => {
    const [timespan, setTimespan] = useState(30);
    const [userTimeSpan, setUserTimeSpan] = useState(30);
    const userBackDate = useMemo(() => formatBackDate(userTimeSpan), [userTimeSpan]);
    const gridBackDate = useMemo(() => formatBackDate(timespan), [timespan]);
    const [page, setPage] = useState(0);
    const [total, setTotal] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [usePagination, setUsePagination] = useState(true);
    const [hasFilters, setHasFilters] = useState(false);

    const { user } = useAuth();

    const gridData = useAsync(async (params) => {
        const nextPage = (params?.page || params?.page === 0) ? params.page : page;
        const offset = getOffset(nextPage, pageSize);

        const requests = await policySearchRequest.getAll(gridBackDate, offset?.offset, offset?.size, hasFilters, formatFilters(filters));
        Array.isArray(requests) ? setTotal(requests[0]?.total) : 0;
        
        return requests.sort((a, b) => {
            const aModified = DateTime.fromISO(a.modified);
            const bModified = DateTime.fromISO(b.modified);

            if (aModified > bModified)
                return -1;
            if (aModified < bModified)
                return 1;
            return 0;
        });
    }, false);
    const userMetrics = useAsync(() => metrics.policySearchCountsByUser(user.id, userBackDate), false);
    const tryonMetrics = useAsync(() => metrics.policySearchCountsByUser(null, userBackDate), false);
    const firmData = useAsync(() => lawfirm.getFirms(), false);

    const [filtersOpen, setFiltersOpen] = useState(false);
    
    const {
        state: { values: filters },
        fieldActions,
        formActions
    } = useForm({
        initialState: {
            filenumber: '',
            ownerName: '',
            submissionStart: null,
            submissionEnd: null,
            address: '',
            county: '',
            statusOptions: [
                { label: 'New', value: 1 },
                { label: 'In Progress', value: 2 },
                { label: 'Review', value: 5 },
                { label: 'Found', value: 3 },
                { label: 'Not Found', value: 4 }
            ],
            status: null,
            id: '',
            completedBy: '',
            firm: null
        },
    });

    let [filteredRows, setFilteredRows] = useState([]);

    const clean = (filters) => {
        return omitBy(filters, (value, key) => value == null || /options/i.test(key));
    };

    const filterData = () => {      
        clean(filters);
        setTimespan(0);
        setHasFilters(true);
        setPage(0);
        setFiltersOpen(false);
        if(timespan == 0)
            gridData.execute();
            
        //Filtering has been moved to the backend...Leaving code below in case in future you want to revert to old functionality. 
        // when logged in as gsgAdmin*, search "All time" when user applies filter, regardless of current Switch value
        /* if (user.roleId === 1) {
            if (timespan === 0) {
                const filtered = applyFilters(gridData.value, clean(filters));
                setFilteredRows(filtered);
            } else {
                // it is enough to change time span here because changes to `timespan` will refresh the grid and apply filters
                setTimespan(0);
                setUsePagination(true);
                setPageSize(1000); 
            }
        } else {
            // non admin user. `timespan` is not changed, so we have to apply filters "manually"
            const filtered = applyFilters(gridData.value, clean(filters));
            setFilteredRows(filtered);
        } 
        setFiltersOpen(false);*/
    };
    
    const clearFilters = () => {
        
        formActions.reset();
        setFilteredRows(gridData.value);
        setFiltersOpen(false);
        setUsePagination(true);
        setPage(0);
        setHasFilters(false);
        setTimespan(30);
    };

    const quickStatusFilter = (statusId) => {
        formActions.reset();
        const status = filters.statusOptions.find(o => o.value == statusId);

        fieldActions.onChange([status], 'status');
        filterData();
    };

    const [attachmentsOpen, setAttachmentsOpen] = useState(false);
    const showAttachmentsModal = () => setAttachmentsOpen(true);
    const hideAttachmentsModal = () => setAttachmentsOpen(false);
    const [attachments, setAttachments] = useState([]);

    const fetchAll = async (params) => gridData.execute(params)
        .then(() => tryonMetrics.execute());

    useEffect(() => firmData.execute(), []);

    useEffect(() => {
        // get metrics because time span has changed
        tryonMetrics.execute();
        userMetrics.execute();
    }, [userTimeSpan]);

    useEffect(() => {
        // fetch data first then apply filter if any is present
        gridData.execute();
    }, [timespan, pageSize]);

    useEffect(() => {
        if (gridData?.value) {
            // if filters are present, apply them
            const filtered = applyFilters(gridData.value, clean(filters));
            setFilteredRows(filtered);
        } else {
            setFilteredRows([]);
        }
    }, [gridData?.value]);

    const handlePageChange = nextPage => {
        setPage(nextPage);
        fetchAll({ page: nextPage });
    };

    const handlePageSizeChange = newPageSize => {
        setPageSize(newPageSize);
        fetchAll();
    };
    
    const ctxValue = {
        gridData,
        firmData,
        userMetrics,
        fetchAll,
        tryonMetrics,
        filteredRows,
        filters,
        fieldActions,
        apply: filterData,
        clear: clearFilters,
        quickStatusFilter,
        timespan,
        setTimespan,
        //setGirdTimeSpan,
        userTimeSpan,
        setUserTimeSpan,
        attachments,
        setAttachments,
        showAttachmentsModal,
        hideAttachmentsModal
    };

    const inProgressCount = useMemo(
        () => Array.isArray(gridData.value) ? gridData.value.filter(r => r.statusId == 2 || r.statusId == 5).length : 0,
        [gridData.status, gridData.value]
    );
    
    const newCount = useMemo(
        () => Array.isArray(gridData.value) ? gridData.value.filter(r => r.statusId == 1).length : 0,
        [gridData.status, gridData.value]
    );

    return (
        <DashboardContext.Provider value={ctxValue}>
            <Header />
            <div className='container pt-4'>
                <div className='grid grid-flow-col'>
                    <div>
                        <h2 className='font-bold text-2xl text-primary-60'>Policy Search Requests</h2>
                        <RefreshIndicator />
                    </div>
                    <div className='flex flex-col py-2 items-end justify-around text-neutral-90 text-sm'>
                        <button 
                            className='hover:text-primary-60 focus:outline-none py-1'
                            onClick={() => quickStatusFilter(2)} >
                            <span className='font-bold'>Searches In Progress</span>
                            &nbsp;&nbsp;{inProgressCount}
                        </button>
                        <button 
                            className='hover:text-primary-60 focus:outline-none py-1'
                            onClick={() => quickStatusFilter(1)}>
                            <span className='font-bold'>New Requests</span>
                            &nbsp;&nbsp;{newCount}
                        </button>
                    </div>
                </div>
                <div className="mt-6 flex items-center justify-between">
                    <button 
                        className='py-2 px-4 flex text-blue-dark text-sm font-semibold bg-gray-50 focus:outline-none items-center justify-center rounded-full'
                        onClick={() => setFiltersOpen(true)}
                    >
                        <MdFilterList size='1.125em' className='mr-1'/>
                        Filter {filtersOpen}
                    </button>
                    <Link to='/policysearch' className='py-2 px-4 flex items-center justify-center bg-blue-50 hover:bg-blue-60 text-white text-sm rounded-full'>
                        <MdBook className="mr-2" size="1.025em"/> New Prior Policy Search
                    </Link>
                </div>
                <Grid 
                    pageSize={pageSize} 
                    currentPage={page} 
                    handlePageChange={handlePageChange} 
                    totalRows={total} 
                    handlePageSizeChange={handlePageSizeChange}
                    usePagination={usePagination} 
                /> 
                <FilterModal isOpen={filtersOpen} setOpen={setFiltersOpen} />
                <AttachmentsModal isOpen={attachmentsOpen} />
            </div>
        </DashboardContext.Provider>
    );
};

export default TryonHome;

function RefreshIndicator() {
    const { fetchAll } = useDashboard();

    const [lastUpdated, setLastUpdated] = useState(DateTime.now());

    const refresh = async () => {
        await fetchAll();
        setLastUpdated(DateTime.now());
    };

    return (
        <div className='flex items-center text-sm mr-auto mt-2'>
            {status != asyncState.IDLE && (
                <>
                    <p className="font-bold">Last Updated</p>
                    <p className="ml-2">{lastUpdated.toFormat("DD 'at' tt")}</p>
                </>
            )}
            {status == asyncState.PENDING 
                ? <AiOutlineLoading3Quarters className='m-auto text-blue-50 animate-spin ml-2'/>
                : (
                    <button 
                        className="flex items-center justify-center text-blue-50 font-semibold p-2 ml-4 focus:outline-none"
                        onClick={refresh}
                    >
                        <MdRefresh className="mr-1" size="1.25em"/>
                        Refresh
                    </button>
                )
            }
        </div>
    );
}

function formatBackDate(timespan) {
    let backDate = DateTime.utc();
    // adding 1 to the .minus below since the stored proc does >= when comparing dates
    switch (timespan) {
    case 7:
        backDate = backDate.minus({ days: 8 });
        break;
    case 30:
        backDate = backDate.minus({ days: 31 }); // TODO: what if the current month has 28 days?
        break;
    case 365:
        backDate = backDate.minus({ days: 366 });
        break;
    case 1:
        break;
    default:
        backDate = backDate.minus({ years: 100 });
    }

    if (backDate != null)
        backDate = backDate.set({ hour: 0, minute: 0, second: 0, millisecond: 0});

    return backDate ? backDate.toISO() : null;
}

function formatFilters(filters) {
    return {
        address: filters?.address || '',
        completedBy: filters?.completedBy || '',
        county: filters?.county || '',
        firmId: Array.isArray(filters?.firm) ? filters?.firm[0].value || '' : '',
        id: +filters?.id || 0,
        ownerName: filters?.ownerName || '',
        statusId: Array.isArray(filters?.status) ? +filters?.status[0].value || 0 : 0,
        submissionStart: filters?.submissionStart,
        submissionEnd: filters?.submissionEnd
    };
}